import React, { useEffect, useState } from "react";
import ShowcaseManager from "@/components/ShowcaseManager";
import ShowcaseSetup from "@/components/ShowcaseSetup";
import ShowcaseBilling from "@/components/ShowcaseBilling";
import BrokeragePageEditor from "@/components/BrokeragePageEditor";
import { useManagedBrokerage } from "@/hooks/useBrokerageManage";
import SelectDropDown from "@/components/Common/SelectDropDown";
import { PICKER_STYLES } from "@/helpers/pickerStyles";
import "./BrokerageStudio.scss";

export { ABOUT_MAX, MAX_FEATURED, aboutErrorOf, draftOf, isDirty } from "@/components/BrokeragePageEditor/model";

export const checkoutReturnOf = (search) => {
  const params = new URLSearchParams(search || "");
  const outcome = params.get("showcase");
  if (outcome !== "done" && outcome !== "canceled") return null;
  return { outcome, sessionId: params.get("session_id") || "" };
};

export const SHOWCASE_SECTIONS = ["billing", "showcase", "setup"];

export const sectionAnchor = (section) => `showcase-${section}`;

const scrollToSection = (section) =>
  document.getElementById(sectionAnchor(section))?.scrollIntoView({ behavior: "smooth", block: "start" });

const clearCheckoutParams = () => {
  const url = new URL(window.location.href);
  ["showcase", "session_id"].forEach((name) => url.searchParams.delete(name));
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
};

export const notManagedNotice = (brokerages, requestedId, name) =>
  requestedId && brokerages.length && !brokerages.some((item) => item.id === requestedId)
    ? `You don't manage that brokerage page, so we opened ${name}.`
    : "";

const BrokerageSwitch = ({ brokerages, selectedId, onSelect }) => {
  if (brokerages.length < 2) return null;
  const options = brokerages.map((item) => ({
    value: item.id,
    label: item.networkName ? `${item.name} (${item.networkName})` : item.name,
  }));
  return (
    <div className="brokerage-studio__switch">
      <span>Brokerage</span>
      <SelectDropDown
        id="brokerage-switch"
        classNamePrefix="select"
        options={options}
        value={options.find((option) => option.value === selectedId)}
        styles={PICKER_STYLES}
        errorClass="d-none"
        onChange={(option) => option && onSelect(Number(option.value))}
      />
    </div>
  );
};

const BrokerageStudio = ({ view = "page", brokerages, selectedId, onSelect, requestedId = null }) => {
  const [checkout, setCheckout] = useState(() => checkoutReturnOf(window.location.search));
  const page = useManagedBrokerage(selectedId).data;
  const current = brokerages.find((item) => item.id === selectedId);
  const name = page?.name || current?.name || "your brokerage";
  const logoUrl = page?.settings?.logoUrl || page?.brandLogoUrl || "";
  const notice = notManagedNotice(brokerages, requestedId, current?.name || name);
  const requestedSection = new URLSearchParams(window.location.search).get("section");

  useEffect(() => {
    if (view === "showcase" && SHOWCASE_SECTIONS.includes(requestedSection) && requestedSection !== "billing") {
      scrollToSection(requestedSection);
    }
  }, [view, requestedSection]);

  return (
    <div className="brokerage-studio-shell">
      {notice ? (
        <p className="brokerage-studio-shell__notice" role="status">
          {notice}
        </p>
      ) : null}
      {view === "page" ? (
        <BrokeragePageEditor
          key={selectedId}
          selectedId={selectedId}
          switcher={<BrokerageSwitch brokerages={brokerages} selectedId={selectedId} onSelect={onSelect} />}
        />
      ) : (
        <div className="brokerage-studio-shell__showcase">
          <BrokerageSwitch brokerages={brokerages} selectedId={selectedId} onSelect={onSelect} />
          <section id={sectionAnchor("billing")}>
            <ShowcaseBilling
              brokerageId={selectedId}
              checkout={checkout}
              onCheckoutHandled={() => {
                clearCheckoutParams();
                setCheckout(null);
              }}
              onGo={scrollToSection}
            />
          </section>
          <section id={sectionAnchor("showcase")}>
            <ShowcaseManager brokerageId={selectedId} brokerageName={name} logoUrl={logoUrl} onGo={scrollToSection} />
          </section>
          <section id={sectionAnchor("setup")}>
            <ShowcaseSetup brokerageId={selectedId} onGo={scrollToSection} />
          </section>
        </div>
      )}
    </div>
  );
};

export default BrokerageStudio;
